/**
 * Textos (ca) de planta — OBR-149: recompte d'existències a l'App: namespace `planta.stockCount`.
 * Tall propi: no es toca `ca.ts`. Les tres llengües han de tenir les mateixes claus (i18n.test.ts) i les mateixes
 * variables. Vocabulari del sector: «Existències», «recompte», mai «Stock».
 */

/**
 * Tipus AMPLE a posta (no `as const`): el tipus literal de TOTS els catàlegs junts passa del que `tsc` pot serialitzar a la declaració
 * (TS7056 a `i18n/index.ts`) i només aquest espai en porta 139 claus. Les claus es comproven igualment: `i18n.test.ts` (les tres llengües
 * amb les mateixes claus i variables) i `i18nUsage.test.ts` (cada `t('planta.stockCount:…')` existeix). Quan l'anotació explícita de
 * `resources` (commit 64cd075, branca del catàleg) sigui a main, es pot tornar a `as const`.
 */
export const plantaCountCa: { readonly 'planta.stockCount': Readonly<Record<string, string>> } = {
  'planta.stockCount': {
    // ── Entrada i llista de recomptes ─
    title: 'Recompte d’existències',
    subtitle: 'Compta el que hi ha a l’obrador. El responsable valida el recompte al BackOffice.',
    entryTitle: 'Recompte d’existències',
    entryBody: 'Escaneja l’etiqueta del lot i compta el que hi ha.',
    entryOpen_one: '{{count}} recompte obert',
    entryOpen_other: '{{count}} recomptes oberts',
    entryNone: 'Cap recompte obert',
    entryUnknown: 'Comprova els recomptes oberts',
    listOpenTitle: 'Recomptes oberts',
    listClosedTitle: 'Tancats',
    emptyOpen: 'No hi ha cap recompte obert',
    emptyOpenHint: 'Quan el responsable en creï un al BackOffice, apareixerà aquí.',
    emptyWeb: 'El recompte es fa des de l’app de l’obrador (tauleta o mòbil).',
    refresh: 'Actualitza',
    refreshing: 'Actualitzant…',
    refreshOffline: 'Sense connexió: es mostra l’última còpia.',
    refreshFailed: 'No s’ha pogut actualitzar: es mostra l’última còpia.',
    refreshUnsynced: 'Hi ha comptatges pendents d’enviar: s’envien primer i després s’actualitza.',
    refreshPartial: 'Algunes dades no s’han pogut llegir i s’han deixat fora.',
    scopeTenant: 'Tot l’obrador',
    scopeNamed_zone: 'Zona · {{name}}',
    scopeNamed_section: 'Secció · {{name}}',
    scopeNamed_location: 'Ubicació · {{name}}',
    scopeNamed_article: 'Article · {{name}}',
    scopeType_zone: 'Una zona',
    scopeType_section: 'Una secció',
    scopeType_location: 'Una ubicació',
    scopeType_article: 'Un article',
    countProgress: '{{counted}} de {{total}} línies comptades',
    countNotLoaded: 'Obre’l per baixar les línies.',
    photoOn: 'Foto del {{date}}',
    statusOpen: 'Obert',
    status_validated: 'Validat',
    status_cancelled: 'Cancel·lat',
    closedOn: 'Tancat el {{date}}',
    unsyncedBadge_one: '{{count}} pendent d’enviar',
    unsyncedBadge_other: '{{count}} pendents d’enviar',
    rejectedBadge_one: '{{count}} no aplicat',
    rejectedBadge_other: '{{count}} no aplicats',
    openCountLabel: 'Obre el recompte {{number}}',
    openMore_one: 'Hi ha {{count}} recompte obert més que no es veu aquí. Actualitza o tanca’n algun per veure’l.',
    openMore_other: 'Hi ha {{count}} recomptes oberts més que no es veuen aquí. Actualitza o tanca’n algun per veure’ls.',
    // ── Pantalla d’un recompte ─
    back: 'Torna als recomptes',
    missingCount: 'Aquest recompte no és a l’aparell. Torna a la llista i actualitza.',
    pendingValidation: 'Pendent de validar: quan acabis, el responsable el valida al BackOffice. Aquí no s’ajusta res.',
    readOnlyValidated: 'Aquest recompte ja s’ha validat al BackOffice. Només es pot consultar.',
    readOnlyCancelled: 'Aquest recompte s’ha cancel·lat. Només es pot consultar.',
    unsyncedNotice_one: '{{count}} comptatge pendent d’enviar. S’enviarà sol en recuperar la connexió.',
    unsyncedNotice_other: '{{count}} comptatges pendents d’enviar. S’enviaran sols en recuperar la connexió.',
    rejectedNotice_one: '{{count}} comptatge no aplicat: el servidor no l’ha pogut registrar (el recompte ja no és obert o el lot no hi encaixa). La quantitat es manté aquí.',
    rejectedNotice_other: '{{count}} comptatges no aplicats: el servidor no els ha pogut registrar (el recompte ja no és obert o el lot no hi encaixa). Les quantitats es mantenen aquí.',
    rejectedAck: 'Entesos',
    truncatedNotice: 'Aquest recompte té més línies de les que l’aparell guarda ({{count}}). Compta la resta des del BackOffice.',
    linesFrom: 'Línies baixades el {{date}}',
    neverPulled: 'Encara no s’han baixat les línies d’aquest recompte. Cal connexió per baixar-les.',
    loadingLines: 'Baixant les línies…',
    noLines: 'Aquest recompte no té cap línia.',
    scan: 'Escaneja',
    addFound: 'Afegeix un lot trobat',
    scanTitle: 'Escaneja l’etiqueta del lot',
    scanHint: 'Apunta al QR de l’etiqueta.',
    filterLabel: 'Filtra les línies',
    filterAll: 'Totes · {{count}}',
    filterPending: 'Pendents · {{count}}',
    filterShort: 'Falta · {{count}}',
    filterOver: 'Sobra · {{count}}',
    filterMatch: 'Coincideix · {{count}}',
    searchPlaceholder: 'Cerca per article, lot o lloc',
    searchLabel: 'Cerca una línia',
    resultsCount_one: '{{count}} línia',
    resultsCount_other: '{{count}} línies',
    emptyNoMatch: 'Cap línia coincideix amb «{{query}}».',
    emptyFilter: 'Cap línia en aquest estat.',
    showMore: 'Mostra’n més',
    resultsShown: 'Es mostren {{shown}} de {{total}}: cerca o filtra per trobar-ne d’altres.',
    // ── Una línia ─
    lotLine: 'Lot {{lot}}',
    noLot: 'Sense lot',
    newLot: 'Lot nou',
    newLotWithCode: 'Lot nou · {{code}}',
    noPlace: 'Sense lloc',
    expiresOn: 'Caduca {{date}}',
    photoQty: 'Foto ({{date}}): {{quantity}}',
    countedQty: 'Comptat: {{quantity}}',
    notCounted: 'Sense comptar',
    foundTag: 'Trobat',
    unsyncedTag: 'Pendent d’enviar',
    rejectedTag: 'No aplicat',
    state_pending: 'Pendent',
    state_match: 'Coincideix',
    state_short: 'Falta {{quantity}}',
    state_over: 'Sobra {{quantity}}',
    lineLabel: '{{article}}, {{state}}',
    // ── El full de quantitat ─
    sheetTitle: 'Compta',
    sheetPhoto: 'Foto del {{date}}: {{quantity}}',
    sheetFound: 'Lot trobat: no era a la foto.',
    sheetCurrent: 'Ja comptat: {{quantity}}',
    quantityLabel: 'Quantitat comptada',
    matchButton: 'Coincideix',
    matchHelp: 'Compta el mateix que diu la foto.',
    save: 'Desa',
    modeSet: 'Substitueix',
    modeAdd: 'Suma',
    modeAddTotal: 'Total: {{quantity}}',
    invalidQuantity: 'Escriu una quantitat vàlida.',
    conflictTitle: 'Una altra persona ja ho havia comptat',
    conflictBody: 'Hi ha {{counted}} comptats per una altra persona. Què vols fer amb els {{typed}} que has escrit?',
    conflictAdd: 'Suma (total {{total}})',
    conflictReplace: 'Substitueix ({{typed}})',
    conflictCancel: 'Cancel·la',
    placesTitle: 'On és aquest lot?',
    placesBody: 'El lot {{lot}} és a més d’un lloc d’aquest recompte. Tria on el compres.',
    // ── Un lot trobat ─
    foundTitle: 'Afegeix un lot trobat',
    foundBody: 'Un lot que no era a la foto del recompte.',
    foundScan: 'Escaneja una etiqueta',
    foundNew: 'Lot nou (sense etiqueta)',
    foundNoLine: 'El lot {{lot}} no és a la llista d’aquest recompte.',
    foundAddIt: 'L’afegeixo com a lot trobat',
    foundOutOfScope: 'El lot {{lot}} és de {{article}}, però és fora de l’abast d’aquest recompte i no s’hi pot afegir.',
    foundKnown: '{{article}} · lot {{lot}}',
    foundWhere: 'On l’has trobat?',
    foundUnknown: 'No coneixem el lot {{lot}}. Se’n crearà un de nou: tria l’article.',
    knownLotsChecking: 'Comprovant el lot {{lot}}…',
    knownLotsCannotTitle: 'No puc comprovar si el lot ja existeix',
    knownLotsCannotBody: 'El lot {{lot}} no és a la llista que té l’aparell i no puc saber si ja existeix (sense connexió, llista antiga o incompleta). Pots tornar-ho a provar amb connexió o comptar-lo com a nou igualment: el número escanejat es desa a la nota i el responsable ho revisarà en validar.',
    knownLotsCountAsNew: 'Compta’l com a nou igualment',
    knownLotsCountAsNewHelp: 'El responsable ho revisarà en validar.',
    knownLotsRetry: 'Torna-ho a provar',
    scannedLotNote: 'Lot escanejat: {{lot}}',
    newLotTitle: 'Lot nou',
    newLotArticle: 'Article',
    newLotPick: 'Tria l’article',
    newLotSearch: 'Cerca un article per nom o codi',
    newLotNoArticles: 'Aquest aparell encara no té articles. Connecta’l i actualitza.',
    newLotNoMatch: 'Cap article coincideix amb «{{query}}».',
    newLotChange: 'Canvia l’article',
    newLotSupplierCode: 'Codi de lot del proveïdor (opcional)',
    newLotExpiry: 'Caducitat',
    newLotExpiryHelp: 'Aquest article demana data de caducitat.',
    newLotQuantity: 'Quantitat trobada',
    newLotHelp: 'El número de lot d’Obrava el crea el servidor en validar el recompte: no cal escriure’l.',
    newLotSave: 'Afegeix el lot',
    issue_untracked: 'Aquest article no porta control d’existències: no hi ha res a comptar.',
    issue_production_article: 'Aquest article es produeix: no se li pot crear un lot nou des d’un recompte. Avisa el responsable.',
    issue_quantity: 'Escriu una quantitat més gran que zero.',
    issue_expiry_required: 'Falta la data de caducitat d’aquest article.',
    // ── Missatges ─
    savedLine: 'Comptat: {{article}} · {{quantity}}',
    savedMerged: 'Ja hi havia una línia d’aquest lot: s’hi ha sumat. {{article}} · +{{quantity}} (total {{total}})',
    errScan: 'No s’ha pogut llegir l’etiqueta. Torna-la a apuntar.',
    errNotOpen: 'Aquest recompte ja no és obert: no es pot comptar.',
    errProvisionalNewLot: 'Aquest lot nou encara no s’ha enviat. Espera que se sincronitzi per canviar-ne la quantitat.',
    errSave: 'No s’ha pogut desar el comptatge. Torna-ho a provar.',
    errStaleLine: 'Aquesta línia s’ha actualitzat mentre la comptaves. Tanca el full i torna-la a obrir.',
    cannotCount: 'El teu rol no pot comptar: pots consultar el recompte, però no desar-hi quantitats.',
  },
};
